import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { z } from "zod";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useCallback, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useMutation } from "@tanstack/react-query";
import { useTRPC } from "../../trpc.js";
import { notifications } from "@mantine/notifications";
export const RenameChatModal = ({ opened, onClose, chatId, currentSummary }) => {
    const intl = useIntl();
    const trpc = useTRPC();
    const { mutateAsync: renameChatMutation } = useMutation(trpc.renameChat.mutationOptions());
    const [isPending, setIsPending] = useState(false);
    const validate = zod4Resolver(z.object({
        summary: z
            .string()
            .min(1, {
            message: intl.formatMessage({
                defaultMessage: "Name is required",
                id: "error.nameRequired",
            }),
        })
            .max(255, {
            message: intl.formatMessage({
                defaultMessage: "Name is too long",
                id: "error.nameTooLong",
            }),
        }),
    }));
    const { onSubmit, getInputProps } = useForm({
        validateInputOnBlur: true,
        initialValues: {
            summary: currentSummary,
        },
        validate,
    });
    const handleRename = useCallback(({ summary }) => {
        const trimmed = summary.trim();
        if (trimmed === currentSummary) {
            onClose();
            return;
        }
        setIsPending(true);
        void renameChatMutation({ chatId, summary: trimmed })
            .then(() => {
            onClose();
        })
            .catch((error) => {
            if (error instanceof Error) {
                notifications.show({
                    title: intl.formatMessage({
                        defaultMessage: "Failed to rename chat",
                        id: "notification.failedToRenameChat",
                    }),
                    message: intl.formatMessage({
                        defaultMessage: "Failed to rename chat",
                        id: "notification.chatRenameError",
                    }),
                });
            }
        })
            .finally(() => {
            setIsPending(false);
        });
    }, [currentSummary, chatId, renameChatMutation, onClose]);
    return (_jsx(Modal, { opened: opened, onClose: onClose, title: intl.formatMessage({ defaultMessage: "Rename chat", id: "title.renameChat" }), children: _jsx("form", { onSubmit: onSubmit(handleRename), children: _jsxs(Stack, { gap: "md", children: [_jsx(TextInput, { label: intl.formatMessage({
                            defaultMessage: "Name",
                            id: "label.name",
                        }), required: true, ...getInputProps("summary") }), _jsxs(Group, { gap: "md", justify: "flex-end", mt: "md", children: [_jsx(Button, { variant: "secondary", onClick: onClose, disabled: isPending, children: _jsx(FormattedMessage, { id: "button.cancel", defaultMessage: "Cancel" }) }), _jsx(Button, { variant: "tertiary", type: "submit", loading: isPending, children: _jsx(FormattedMessage, { id: "button.rename", defaultMessage: "Rename" }) })] })] }) }) }));
};
